import React, { useCallback, useState } from "react";
import { Button, Modal, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { apiConfig } from "../authConfig";
import { resolveReadableBlobUrl } from "../utils/blobPreview";
import { stripTrailingDocumentExtension } from "../utils/artefactDisplayFileName";
import { ArtefactTemplatePreview } from "./ArtefactTemplatePreview";

/** @param {string} fileLabel @param {'original' | 'branded'} kind */
function downloadFilename(fileLabel, kind) {
  const raw = String(fileLabel || "template").trim() || "template";
  const dot = raw.lastIndexOf(".");
  const suffix = kind === "original" ? "-original" : "-branded";
  if (dot <= 0 || dot === raw.length - 1) {
    return `${raw}${suffix}`;
  }
  const stem = raw.slice(0, dot);
  const ext = raw.slice(dot);
  return `${stem}${suffix}${ext}`;
}

/**
 * Side-by-side Original vs Branded preview (dual render, not structural diff).
 * @param {boolean} [hideDownloads=false] When true (e.g. advisor create artefacts), previews only — no download buttons.
 */
export function ArtefactBrandedCompareModal({
  open,
  onClose,
  fileLabel,
  originalBlobUrl,
  brandedBlobUrl,
  getApiToken,
  hideDownloads = false,
}) {
  const [downloadKind, setDownloadKind] = useState(null);

  const handleDownload = useCallback(
    async (kind) => {
      const rawUrl = kind === "original" ? originalBlobUrl : brandedBlobUrl;
      if (!rawUrl) {
        message.warning(kind === "original" ? "No original file to download." : "No branded file to download.");
        return;
      }
      setDownloadKind(kind);
      try {
        const readable = await resolveReadableBlobUrl(apiConfig.baseUrl, rawUrl, getApiToken);
        if (!readable) {
          message.error("Could not resolve a download link for this file.");
          return;
        }
        const res = await fetch(readable);
        if (!res.ok) {
          message.error(`Download failed (${res.status}).`);
          return;
        }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = downloadFilename(fileLabel, kind);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        console.error(e);
        message.error("Download failed. Try opening the file in a new tab from the table.");
      } finally {
        setDownloadKind(null);
      }
    },
    [originalBlobUrl, brandedBlobUrl, fileLabel, getApiToken],
  );

  return (
    <Modal
      title={
        fileLabel
          ? `Compare - ${String(stripTrailingDocumentExtension(String(fileLabel).trim()) || String(fileLabel)).slice(0, 72)}`
          : "Compare templates"
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(1820px, calc(100vw - 24px))"
      height="calc(90vh - 48px)"
      centered
      destroyOnClose
      styles={{
        // antd v6 semantic styles: there is no `content` slot — use `container` + `height` prop on Modal.
        container: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
        },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: "10px 16px 12px",
        },
      }}
    >
      <p className="mb-3 shrink-0 text-xs font-medium text-[color:var(--color-text-muted)]">
        {hideDownloads
          ? "Original (left) and branded copy (right). Office previews may differ slightly from source files."
          : "Original (left) and branded copy (right). Office previews may differ slightly from downloaded files."}
      </p>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
        <div className="flex min-h-[280px] flex-1 flex-col gap-2 lg:min-h-0">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Original</p>
            {!hideDownloads ? (
              <Button
                type="default"
                size="small"
                icon={<DownloadOutlined />}
                disabled={!originalBlobUrl}
                loading={downloadKind === "original"}
                onClick={() => void handleDownload("original")}
              >
                Download original
              </Button>
            ) : null}
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-2">
            {originalBlobUrl ? (
              <ArtefactTemplatePreview
                fillParent
                forceLoad
                variant="inline"
                blobUrl={originalBlobUrl}
                fileLabel={fileLabel}
                getApiToken={getApiToken}
              />
            ) : (
              <p className="p-4 text-xs text-[color:var(--color-text-muted)]">No original URL</p>
            )}
          </div>
        </div>
        <div className="flex min-h-[280px] flex-1 flex-col gap-2 lg:min-h-0">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)]">Branded</p>
            {!hideDownloads ? (
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                disabled={!brandedBlobUrl}
                loading={downloadKind === "branded"}
                onClick={() => void handleDownload("branded")}
              >
                Download branded
              </Button>
            ) : null}
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] p-2">
            {brandedBlobUrl ? (
              <ArtefactTemplatePreview
                fillParent
                forceLoad
                variant="inline"
                blobUrl={brandedBlobUrl}
                fileLabel={fileLabel}
                getApiToken={getApiToken}
              />
            ) : (
              <p className="p-4 text-xs text-[color:var(--color-text-muted)]">No branded URL</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
